import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button, Col, Dropdown, DropdownButton, OverlayTrigger, Popover, Row, Toast, ToastContainer, Tooltip
} from 'react-bootstrap-5';
import { PropertyBookmarkWrapper as PnPropertyBookmarkWrapper } from '../pn_support/property_bookmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faRectangleHistoryCirclePlus, faTimes, faUserCheck, faUserTag } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import { addToWatchList, claimProperty, removeClaimedProperty, removeFromWatchlist } from '../../helpers/connect_api';
import { default as PnAddToList } from '../pn_support/add_to_list';
import AddToList from '../properties/add_to_list';
import PropertyBookmark from '../search/components/results/property_bookmark';
import { PROPERTY_MODEL } from '../../constants';

export default function PropertyActions({
  address,
  bookmarked,
  clarityMerge,
  canWatchlist,
  connectPropertyId,
  insightsPropertyId,
  claimed,
  initialWatchlistPropertyId,
  products
}) {
  const target = useRef(null);
  const [claimedProperty, setClaimedProperty] = useState(claimed);
  const [watchlistPropertyId, setWatchlistPropertyId] = useState(initialWatchlistPropertyId);
  const [showAddedToWatchlistMessage, setShowAddedToWatchlistMessage] = useState(false);
  const [showRemovedFromWatchlistMessage, setShowRemovedFromWatchlistMessage] = useState(false);

  const AddToListComponent = clarityMerge ? AddToList : PnAddToList;
  const watchlistAvailable = canWatchlist && !claimedProperty;

  const handleAddToWatchlist = useCallback((e) => {
    e.preventDefault();
    addToWatchList(insightsPropertyId)
      .then(({ id }) => {
        setWatchlistPropertyId(id);
        setShowAddedToWatchlistMessage(true);
        setShowRemovedFromWatchlistMessage(false);
      });
  }, []);

  const handleRemoveFromWatchlist = useCallback((e) => {
    e.preventDefault();
    removeFromWatchlist(watchlistPropertyId).then(() => {
      setWatchlistPropertyId();
      setShowRemovedFromWatchlistMessage(true);
      setShowAddedToWatchlistMessage(false);

      // Bit of a hack, but this watchlist info card is not controlled by react. If the user removes the property
      // from the watchlist, manually delete this dom element
      document.getElementById('js-watchlist-info')?.remove();
    });
  }, [watchlistPropertyId]);

  const handleClaimProperty = useCallback(() => {
    claimProperty(insightsPropertyId).then(() => setClaimedProperty(true));
  }, []);

  const handleRemoveClaimedProperty = useCallback(() => {
    removeClaimedProperty(connectPropertyId).then(() => setClaimedProperty(false));
  }, []);

  const popover = useMemo(() => {
    return (
      <Popover id="popover-basic">
        <Popover.Header as="h3">Claim this property</Popover.Header>
        <Popover.Body>
          <p>
            I agree as follows:<br />
            (i) I am (or I have the authority to act on behalf of) the owner of this property;<br />
            (ii) I will not provide incorrect information or state a discriminatory preference;<br />
            and (iii) I will comply with the Buildout Terms of Use.
          </p>
          <Button className="secondary" onClick={handleClaimProperty}>
            Submit
          </Button>
        </Popover.Body>
      </Popover>
    );
  });

  return (
    <Row className="gx-2">
      {products.prospecting && (
        <Col xs="auto">
          {clarityMerge ? (
            <PropertyBookmark bookmarked={bookmarked} id={insightsPropertyId}/>
          ) : (
            <PnPropertyBookmarkWrapper id={insightsPropertyId} />
          )}
        </Col>
      )}
      <Col xs="auto">
        {claimedProperty && (
          <OverlayTrigger overlay={<Tooltip>Unclaim Property</Tooltip>} placement="auto">
            <Button variant="text-primary" onClick={handleRemoveClaimedProperty}>
              <FontAwesomeIcon icon={faUserCheck}/>
            </Button>
          </OverlayTrigger>
        )}
        {!claimedProperty && (
          <OverlayTrigger overlay={<Tooltip>Claim Property</Tooltip>} placement="auto">
            <div>
              <OverlayTrigger overlay={popover} placement="bottom" trigger="click">
                <Button variant="text-primary"><FontAwesomeIcon icon={faUserTag} /></Button>
              </OverlayTrigger>
            </div>
          </OverlayTrigger>
        )}
      </Col>
      {(watchlistAvailable || products.prospecting) && (
        <Col xs="auto">
          {watchlistAvailable && products.prospecting && (
            <DropdownButton id="dropdown-basic-button" ref={target} title="Add To List" variant="secondary">
              <AddToListComponent
                itemCount={1}
                itemIds={new Set([insightsPropertyId])}
                itemType={PROPERTY_MODEL}
                popoverTargetRef={target}
                tooltip={false}
              >
                <Dropdown.Item>
                  <FontAwesomeIcon className="me-1" fixedWidth icon={faRectangleHistoryCirclePlus}/> Add To Call List
                </Dropdown.Item>
              </AddToListComponent>
              {!watchlistPropertyId && (
                <Dropdown.Item href="#" onClick={handleAddToWatchlist}>
                  <FontAwesomeIcon className="me-1" fixedWidth icon={faEye}/>Add To Watchlist
                </Dropdown.Item>
              )}
              {watchlistPropertyId && (
                <Dropdown.Item href="#" onClick={handleRemoveFromWatchlist}>
                  <FontAwesomeIcon className="me-1" fixedWidth icon={faTimes}/>Remove From Watchlist
                </Dropdown.Item>
              )}
            </DropdownButton>
          )}
          {!watchlistAvailable && products.prospecting && (
            <AddToListComponent
              itemCount={1}
              itemIds={new Set([insightsPropertyId])}
              itemType={PROPERTY_MODEL}
              tooltip={false}
            >
              <Button variant="secondary">
                <FontAwesomeIcon className="me-1" icon={faRectangleHistoryCirclePlus}/> Add To Call List
              </Button>
            </AddToListComponent>
          )}
          {!products.prospecting && watchlistAvailable && (
            <React.Fragment>
              {!watchlistPropertyId && (
                <Button variant="secondary" onClick={handleAddToWatchlist}>
                  <FontAwesomeIcon className="me-1" fixedWidth icon={faEye}/>Add To Watchlist
                </Button>
              )}
              {watchlistPropertyId && (
                <Button variant="secondary" onClick={handleRemoveFromWatchlist}>
                  <FontAwesomeIcon className="me-1" fixedWidth icon={faTimes}/>Remove From Watchlist
                </Button>
              )}
            </React.Fragment>
          )}

          <ToastContainer className="p-3" position="bottom-end" style={{ zIndex: 4 }}>
            <Toast show={showAddedToWatchlistMessage} onClose={() => setShowAddedToWatchlistMessage(false)}>
              <Toast.Header cla>
                <strong className="me-auto">Added To Watchlist</strong>
              </Toast.Header>
              <Toast.Body className="bg-white">
                <p>
                  <strong>We've noted your interest in acquiring {address}</strong>
                </p>
                <p>
                  We will work with our broker network to make this property available to you as soon as we can.
                  There is no guarantee that the property owner will decide to list their property with our network,
                  but if they do, you will be contacted immediately.
                </p>
                <a href="/connect/profile" target="_blank">Update Contact Information</a>
              </Toast.Body>
            </Toast>
            <Toast
              autohide
              show={showRemovedFromWatchlistMessage}
              onClose={() => setShowRemovedFromWatchlistMessage(false)}
            >
              <Toast.Header>
                <strong className="me-auto">Removed From Watchlist</strong>
              </Toast.Header>
            </Toast>
          </ToastContainer>
        </Col>
      )}
    </Row>
  );
}

PropertyActions.propTypes = {
  address: PropTypes.string.isRequired,
  bookmarked: PropTypes.bool.isRequired,
  canWatchlist: PropTypes.bool.isRequired,
  claimed: PropTypes.bool.isRequired,
  clarityMerge: PropTypes.bool.isRequired,
  connectPropertyId: PropTypes.number,
  initialWatchlistPropertyId: PropTypes.number,
  insightsPropertyId: PropTypes.number.isRequired,
  products: PropTypes.shape({
    prospecting: PropTypes.bool.isRequired
  }).isRequired
};
